import { useState, useEffect, useCallback, useRef } from 'react';
import { formatDate } from '../lib/dates';
import { api, asList } from '../lib/api';
import { newsApi, kindEmoji, type FeedCard } from '../lib/newsApi';
import { AuthImage } from '../components/AuthImage';
import { useT } from '../context/I18nContext';

/* ============================================================================
   THE FEED — what a parent or teacher actually sees.

   The same endpoint serves both: the audience rules decide what lands in whose
   feed. A parent sees "Class 9 parents" announcements only if they have a child
   in Class 9; a teacher sees staff ones. Nobody sees a message not addressed to
   them.
   ========================================================================== */

interface FeedItem {
  id: string; title: string; body: string;
  priority: string; sentAt: string; isRead: boolean;
}

/* Cut at a word boundary, not mid-syllable. "four percentage poi…" reads like a
   bug; "four percentage…" reads like an excerpt. */
function excerpt(body: string, max = 100): string {
  if (body.length <= max) return body;
  const cut = body.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return (space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd() + '…';
}

/* A news card. The body used to be sliced to 100 characters with an ellipsis and
   no way to see the rest — the card was a plain div, so the remaining text was
   unreachable. Now the excerpt is an excerpt of something you can open. */
function NewsCard({ n }: { n: FeedCard }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const image = n.media.find((m) => m.kind === 'Image');
  const body = n.body ?? '';
  const hasMore = excerpt(body) !== body;

  return (
    <div className="card" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {image && <AuthImage url={image.url} style={{ width: '100%', height: 130, objectFit: 'cover' }} />}
      {/* ⚠ flex:1 + column, so the date below can be pushed to the bottom edge. Grid rows stretch
          every card to the tallest in the row; without this, a card with no cover image (or a short
          body) ends with a block of dead space under its date rather than a tidy baseline. */}
      <div style={{ padding: 14, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 15 }}>{kindEmoji(n.kind)}</span>
          {n.kind}{n.pinned && ' · 📌'}
        </div>
        {n.highlight && (
          // ⚠ alignSelf: the parent is now a flex column, whose default align-items is `stretch` —
          // an inline-block pill would be stretched to the card's full width and stop reading as a pill.
          <div style={{ display: 'inline-block', alignSelf: 'flex-start', marginTop: 6, padding: '2px 10px', borderRadius: 999, background: 'var(--brand)', color: '#fff', fontSize: 12, fontWeight: 700 }}>
            {n.highlight}
          </div>
        )}
        <div style={{ fontWeight: 600, marginTop: 6, fontFamily: 'var(--font-display)' }}>{n.title}</div>
        {n.studentName && <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>{n.studentName}</div>}
        {body && (
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 6, whiteSpace: 'pre-wrap' }}>
            {open ? body : excerpt(body)}
          </div>
        )}
        {hasMore && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            style={{
              // ⚠ alignSelf + textAlign for the same reason as the pill above: stretched by the flex
              // column, this button's label would centre itself under the body text.
              alignSelf: 'flex-start', textAlign: 'left',
              marginTop: 6, padding: 0, border: 0, background: 'none', cursor: 'pointer',
              font: 'inherit', fontSize: 12, color: 'var(--ink-3)', textDecoration: 'underline',
            }}
          >
            {open ? t('Show less') : t('Tap to read more')}
          </button>
        )}
        {/* ⚠ marginTop:auto — the date is the card's footer and sits on the bottom edge whatever
            the card's height. A news post with no photograph is normal, not a broken one, so it
            should look deliberate rather than truncated. */}
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 'auto', paddingTop: 8 }}>{new Date(n.publishedAt).toLocaleDateString('en-IN')}</div>
      </div>
    </div>
  );
}

/* A message row. The "Tap to read more" hint used to key off `body.length > 100`
   while the text was clamped to two lines — so a short body that happened to wrap
   to three lines was cut with no affordance at all. Measure the clamp instead of
   guessing at it. */
function FeedRow({ item, isOpen, onOpen }: { item: FeedItem; isOpen: boolean; onOpen: () => void }) {
  const t = useT();
  const bodyRef = useRef<HTMLParagraphElement | null>(null);
  const [clamped, setClamped] = useState(false);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const check = () => setClamped(el.scrollHeight > el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [item.body, isOpen]);

  return (
    <button
      onClick={onOpen}
      className="card"
      style={{
        padding: 18, textAlign: 'left', cursor: 'pointer', width: '100%',
        borderLeft: item.priority === 'Urgent' ? '3px solid var(--brand)' : undefined,
        background: item.isRead ? undefined : 'var(--surface-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        {!item.isRead && (
          <span aria-label="Unread" style={{
            width: 7, height: 7, borderRadius: '50%',
            background: 'var(--brand)', flexShrink: 0,
          }} />
        )}
        <strong style={{ fontSize: 16, fontFamily: 'var(--font-display)' }}>
          {item.priority === 'Urgent' && '⚠ '}{item.title}
        </strong>
        <span className="mono" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)', flexShrink: 0 }}>
          {formatDate(item.sentAt)}
        </span>
      </div>

      <p ref={bodyRef} style={{
        color: 'var(--ink-2)', fontSize: 14, marginTop: 6,
        whiteSpace: 'pre-wrap',
        ...(isOpen ? {} : {
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }),
      }}>
        {item.body}
      </p>

      {(isOpen || clamped) && (
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          {isOpen ? t('Show less') : t('Tap to read more')}
        </span>
      )}
    </button>
  );
}

export function FeedPage() {
  const t = useT();
  const [items, setItems] = useState<FeedItem[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [news, setNews] = useState<FeedCard[]>([]);

  const load = useCallback(() => {
    api.get<FeedItem[]>('/api/feed').then(setItems).catch(() => setItems([]));
    newsApi.feed(20).then((r) => setNews(asList(r))).catch(() => setNews([]));
  }, []);
  useEffect(load, [load]);

  // Opening it IS reading it. Idempotent on the server, so a second open isn't a second read.
  const openItem = async (item: FeedItem) => {
    setOpen(open === item.id ? null : item.id);
    if (!item.isRead) {
      await api.post(`/api/feed/${item.id}/read`, {}).catch(() => {});
      setItems((prev) => prev?.map((i) => i.id === item.id ? { ...i, isRead: true } : i) ?? null);
    }
  };

  if (!items) {
    return (
      <div>
        <h1 className="h-display" style={{ fontSize: 28, marginBottom: 20 }}>{t('From the school')}</h1>
        {[0, 1, 2].map((i) => (
          <div key={i} className="card" style={{ padding: 18, marginBottom: 10 }}>
            <span className="sk" style={{ width: '45%', marginBottom: 10 }} />
            <span className="sk" style={{ width: '80%' }} />
          </div>
        ))}
      </div>
    );
  }

  const unread = items.filter((i) => !i.isRead).length;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Communication</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>{t('From the school')}</h1>
        {unread > 0 && (
          <p style={{ color: 'var(--ink-2)', marginTop: 4 }}>
            {t('You have')} <strong>{unread}</strong> {t('unread')} {unread === 1 ? t('message') : t('messages')}.
          </p>
        )}
      </div>

      {news.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>🏆 {t('School news & achievements')}</div>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', maxWidth: 900 }}>
            {news.map((n) => <NewsCard key={n.id} n={n} />)}
          </div>
        </div>
      )}

      {items.length === 0 && news.length === 0 ? (
        <div className="empty">
          <h3>{t('Nothing yet')}</h3>
          <p>{t('Announcements from the school will appear here.')}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 640 }}>
          {items.map((i) => (
            <FeedRow key={i.id} item={i} isOpen={open === i.id} onOpen={() => void openItem(i)} />
          ))}
        </div>
      )}
    </div>
  );
}
